/**
 * The request half of ИИ-подбор: ask, wait, land on a proposal or on one of the
 * five failure screens.
 *
 * Shared by both surfaces (onboarding modal, saved-search form) because the
 * rules are identical and subtle enough that a second copy would drift:
 *
 *  - the quota counter is visible BEFORE the button is pressed, which means
 *    fetching it on mount rather than reading it off the first answer;
 *  - the quota shown afterwards is the one the SERVER reports, never a local
 *    decrement — a timeout, an error or an empty answer costs nothing, and only
 *    the server knows which of those happened;
 *  - a failure is a code, not a message. The server's `message` is English and
 *    written for a log; every screen here has its own copy.
 */
import { useCallback, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  aiErrorCode,
  aiErrorQuota,
  getQuota,
  propose,
  quotaLeft,
  toAiLocale,
  type AiErrorCode,
  type AiProposeResponse,
  type AiQuota,
  type AiSurface,
} from './ai-search-client';

export type AiPhase = 'idle' | 'loading' | 'ready' | 'failed';

/** What the user asked with — kept so «Повторить» does not need the form. */
export interface AiAsk {
  text?: string;
  tin?: string;
}

/** Shared cache key: both surfaces read the same allowance. */
export const AI_QUOTA_KEY = ['ai-quota'] as const;

export interface UseAiPropose {
  phase: AiPhase;
  result: AiProposeResponse | null;
  /**
   * Which screen to show when `phase === 'failed'`. `'unknown'` covers a
   * NetworkError, an unexpected status and anything else that is not one of
   * the contract's codes — all of which mean "not the user's fault, nothing
   * was charged", i.e. the «ИИ не ответил» card with a retry.
   */
  failure: AiErrorCode | 'unknown' | null;
  quota: AiQuota | null;
  /** Proposals left. 0 when quota is unknown — the counter is a promise, and
   *  promising an allowance we have not been told about is the worse error. */
  left: number;
  ask: (input: AiAsk) => void;
  retry: () => void;
  reset: () => void;
}

/**
 * @param surface  which screen is asking — the server splits every metric by it
 * @param locale   UI locale, as the API takes it (see `toAiLocale`)
 * @param enabled  false keeps this completely inert, including the quota fetch.
 *                 With the flag off nothing here may touch the network.
 */
export function useAiPropose(surface: AiSurface, locale: string, enabled: boolean): UseAiPropose {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<AiPhase>('idle');
  const [result, setResult] = useState<AiProposeResponse | null>(null);
  const [failure, setFailure] = useState<AiErrorCode | 'unknown' | null>(null);
  const lastAsk = useRef<AiAsk | null>(null);
  /** Only the newest request may write state — a slow first attempt must not
   *  overwrite the answer to a retry the user has already seen. */
  const reqId = useRef(0);

  const { data: quotaFromServer } = useQuery({
    queryKey: AI_QUOTA_KEY,
    queryFn: getQuota,
    enabled,
    staleTime: 60_000,
    // A quota we cannot read is not a reason to show an error: the button stays
    // usable and the server refuses if there is genuinely nothing left.
    retry: false,
  });

  const [quotaFromAnswer, setQuotaFromAnswer] = useState<AiQuota | null>(null);
  const quota = quotaFromAnswer ?? quotaFromServer ?? null;

  const run = useCallback(
    (input: AiAsk) => {
      const id = ++reqId.current;
      lastAsk.current = input;
      setPhase('loading');
      setFailure(null);
      propose({ ...input, locale: toAiLocale(locale), surface })
        .then((res) => {
          if (reqId.current !== id) return;
          setResult(res);
          setQuotaFromAnswer(res.quota);
          // Keep the shared cache in step so the OTHER surface's counter is
          // right the moment it opens, without a second round trip.
          qc.setQueryData(AI_QUOTA_KEY, res.quota);
          setPhase('ready');
        })
        .catch((e: unknown) => {
          if (reqId.current !== id) return;
          const code = aiErrorCode(e);
          setFailure(code ?? 'unknown');
          // A refusal can carry the quota that caused it. When it does, show
          // that; when it does not, leave the previous figure alone rather than
          // inventing one — see `aiErrorQuota` for why it can be missing.
          const q = aiErrorQuota(e);
          if (q) {
            setQuotaFromAnswer(q);
            qc.setQueryData(AI_QUOTA_KEY, q);
          } else if (code === 'ai_quota_exhausted' && quota) {
            // The server says there is nothing left; reflect that even without
            // numbers, so the counter cannot keep claiming an attempt exists.
            const spent = { ...quota, used: quota.limit };
            setQuotaFromAnswer(spent);
            qc.setQueryData(AI_QUOTA_KEY, spent);
          }
          setPhase('failed');
        });
    },
    [locale, qc, quota, surface],
  );

  const retry = useCallback(() => {
    if (lastAsk.current) run(lastAsk.current);
  }, [run]);

  const reset = useCallback(() => {
    reqId.current++;
    setPhase('idle');
    setResult(null);
    setFailure(null);
  }, []);

  return {
    phase,
    result,
    failure,
    quota,
    left: quotaLeft(quota),
    ask: run,
    retry,
    reset,
  };
}
